<script>
/**
 * WatchlistDetailsModal — read-only "Watchlist Details & Documents" review.
 *
 * Wired: data comes from the parent (via the `flag` and `decision` props, fetched from
 * getWatchlistDecisionDetails), and Download calls downloadWatchlistDocument for a fresh signed URL.
 */
import { peddlrWalletKycLiteMethods } from "@/state/helpers";

function wlFormatFileSize(bytes) {
  if (bytes === null || bytes === undefined) return "";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
}

export default {
  name: "WatchlistDetailsModal",
  props: {
    value: { type: Boolean, default: false },
    // watchlist-get-flag shape (flagged record + source docs)
    flag: { type: Object, default: () => ({}) },
    // watchlist-decision-details shape (decision + supporting docs + reviewer)
    decision: { type: Object, default: () => ({}) },
  },
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    notesLabel() {
      return this.decision && this.decision.decision === "blocked"
        ? "Blocking Regulatory Notes:"
        : "Approval Notes:";
    },
    supportingDocuments() {
      return (this.decision && this.decision.supportingDocuments) || [];
    },
    sourceDocuments() {
      return (this.flag && this.flag.sourceDocuments) || [];
    },
  },
  methods: {
    ...peddlrWalletKycLiteMethods,
    formatSize(bytes) {
      return wlFormatFileSize(bytes);
    },
    onDownload(doc) {
      this.downloadWatchlistDocument({ documentId: doc.documentId })
        .then((res) => {
          if (res && res.url) window.open(res.url, "_blank");
        })
        .catch((err) => {
          console.log("Watchlist document download failed:", err);
          this.$toast && this.$toast.showToast
            ? this.$toast.showToast("error", "Download failed", `Unable to download ${doc.fileName}.`)
            : null;
        });
    },
    onClose() {
      this.show = false;
    },
  },
};
</script>

<template>
  <b-modal v-model="show" size="lg" centered hide-header hide-footer no-close-on-backdrop>
    <div class="wl-details">
      <div class="d-flex justify-content-between align-items-start mb-3">
        <h5 class="fw-bold mb-0">Watchlist Details &amp; Documents</h5>
        <button type="button" class="btn-close-x" @click="onClose" aria-label="Close">
          <i class="bx bx-x"></i>
        </button>
      </div>

      <!-- Flagged record -->
      <div class="wl-record-card">
        <p class="wl-record-headline mb-2">
          AML &amp; WATCHLIST: POTENTIAL NAME MATCH DETECTED
        </p>
        <p class="small text-muted mb-3">Watchlist ID: {{ flag.watchlistId }}</p>
        <!-- The Onfido watchlist feed provides the match summary (record / source / match type / category),
             not the matched entity's name or DOB. Render what's available. -->
        <div class="row">
          <div class="col-md-4">
            <p class="wl-label mb-1">Record</p>
            <p class="wl-value mb-0">{{ flag.recordText || "--" }}</p>
          </div>
          <div class="col-md-4">
            <p class="wl-label mb-1">Date of Birth</p>
            <p class="wl-value mb-0">
              {{ flag.targetDob || "--" }}<template v-if="flag.age"> (Age {{ flag.age }})</template>
            </p>
          </div>
          <div class="col-md-4">
            <p class="wl-label mb-1">Source</p>
            <p class="wl-value mb-0">{{ flag.source || "--" }}</p>
          </div>
        </div>
        <div class="row mt-2">
          <div class="col-md-6">
            <p class="wl-label mb-1">Match Type</p>
            <p class="wl-value mb-0">{{ flag.matchType || "--" }}</p>
          </div>
          <div class="col-md-6">
            <p class="wl-label mb-1">Category</p>
            <p class="wl-value mb-0">{{ flag.categories || "--" }}</p>
          </div>
        </div>

        <div class="d-flex justify-content-between align-items-center mt-3 mb-2">
          <span class="wl-section-title">WATCHLIST SOURCE DOCUMENTS &amp; EVIDENCE</span>
          <span class="small text-muted">
            {{ flag.recordsCount || sourceDocuments.length }}
            {{ (flag.recordsCount || sourceDocuments.length) === 1 ? 'Record' : 'Records' }}
          </span>
        </div>
        <div class="d-flex flex-wrap gap-2" v-if="sourceDocuments.length">
          <!-- Clickable when the feed provides an external URL; plain evidence chip otherwise. -->
          <component
            :is="src.url ? 'a' : 'span'"
            v-for="(src, i) in sourceDocuments" :key="i"
            :href="src.url || null"
            :target="src.url ? '_blank' : null"
            :rel="src.url ? 'noopener' : null"
            class="wl-source-chip">
            {{ src.label }}
            <span v-if="src.url" class="wl-view">View <i class="bx bx-link-external"></i></span>
          </component>
        </div>
        <div class="small text-muted" v-else>No source documents available.</div>
      </div>

      <!-- Supporting documents -->
      <div class="mt-4">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h6 class="fw-bold mb-0">Supporting Documents &amp; Evidence</h6>
          <span class="small text-muted">({{ supportingDocuments.length }} attached)</span>
        </div>
        <div class="wl-doc-row" v-for="(doc, i) in supportingDocuments" :key="i">
          <i class="bx bxs-file wl-doc-icon"></i>
          <div class="wl-doc-body">
            <div class="wl-doc-name">{{ doc.fileName }}</div>
            <div class="small text-muted">{{ formatSize(doc.sizeBytes) }}</div>
          </div>
          <a href="javascript:void(0);" class="wl-download" @click="onDownload(doc)">
            Download <i class="bx bx-download"></i>
          </a>
        </div>
      </div>

      <!-- Notes + reviewer -->
      <div class="mt-4">
        <p class="fw-semibold mb-1">{{ notesLabel }}</p>
        <p class="mb-0">{{ decision.decisionNotes || "--" }}</p>
      </div>

      <div class="d-flex justify-content-between align-items-center mt-4">
        <span class="small text-muted">Review by {{ decision.reviewedByEmail || "--" }}</span>
        <b-button type="button" variant="light" class="wl-close-btn" @click="onClose">
          Close
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<style scoped>
.wl-details {
  padding: 0.25rem 0.25rem 0.5rem;
}
.btn-close-x {
  border: none;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  color: #6c757d;
  cursor: pointer;
}
.wl-record-card {
  border: 1px solid #eee;
  border-radius: 10px;
  padding: 1rem 1.25rem;
}
.wl-record-headline {
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.02em;
}
.wl-section-title {
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.02em;
}
.wl-label {
  font-size: 12px;
  color: #8a8a8a;
}
.wl-value {
  font-weight: 500;
  font-size: 14px;
}
.wl-source-chip {
  border: 1px solid #c4aacf; /* primary-200 */
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 13px;
  color: #333;
  text-decoration: none;
  background: #fff;
}
.wl-view {
  color: #e83092; /* secondary-500 */
  font-weight: 600;
  margin-left: 4px;
}
.wl-doc-row {
  display: flex;
  align-items: center;
  padding: 0.6rem 0;
  border-bottom: 1px solid #f0f0f0;
}
.wl-doc-icon {
  font-size: 22px;
  color: #e83092; /* secondary-500 */
  margin-right: 0.6rem;
}
.wl-doc-body {
  flex: 1;
  min-width: 0;
}
.wl-doc-name {
  font-weight: 500;
  font-size: 14px;
}
.wl-download {
  color: #e83092; /* secondary-500 */
  font-weight: 600;
  text-decoration: none;
}
.wl-close-btn {
  border: 1px solid #d9d9d9;
  min-width: 90px;
}
.gap-2 {
  gap: 0.5rem;
}
</style>
